import { useEffect } from 'react';

/* ──────────────────────────────────────────────────────────────────────────
 * Dependency-free SEO: writes <title>, meta, canonical, Open Graph / Twitter,
 * and optional JSON-LD structured data directly into <head>. Cleans up its own
 * JSON-LD on unmount so pages don't leak schema into each other.
 * ────────────────────────────────────────────────────────────────────────── */

function setMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function Seo({
  title,
  description,
  image,
  type = 'website',
  jsonLd,
  siteName = 'MOZI Innovators',
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${siteName}` : siteName;
    document.title = fullTitle;

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:site_name', siteName);
    if (image) setMeta('property', 'og:image', image);
    setMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', description);
    if (image) setMeta('name', 'twitter:image', image);

    if (typeof window !== 'undefined') {
      setMeta('property', 'og:url', window.location.href);
      setLink('canonical', window.location.href.split('#')[0]);
    }

    let script;
    if (jsonLd) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonLd);
      script.setAttribute('data-seo', 'page');
      document.head.appendChild(script);
    }
    return () => {
      if (script) document.head.removeChild(script);
    };
  }, [title, description, image, type, jsonLd, siteName]);

  return null;
}
